// Email meeting panel (spec §3.5, Phase 6 Task 5 + final fix wave): loads default recipients,
// lets the user edit them, previews the client-facing email, and sends it from the shared mailbox
// through Graph — then logs the send so the meeting moves to "sent". A ref (not just `disabled`)
// guards against a double-click firing two sends while the first confirm handler is still
// mid-flight, and a fresh meeting fetch right before sending catches a conflict (already sent, new
// review flags, or content that drifted since the preview was opened) before anything goes out.
import React, { useEffect, useMemo, useRef, useState } from "react";
import { colors } from "../utils/theme.js";
import { api } from "../api/client.js";
import { primaryBtn, ghostBtn, inputStyle } from "../clients/ui.js";
import ConfirmDialog from "../clients/ConfirmDialog.jsx";
import { renderMeetingEmail, defaultSubject } from "./emailHtml.js";
import { defaultRecipients, addRecipient, confirmLine, recipientCount } from "./recipients.js";
import { sendMeetingEmail, SEND_RECONNECT } from "./sendMail.js";
import { SHARED_MAILBOX } from "./config.js";
import { getGraphToken, connectMicrosoft365 } from "./graph.js";
import { computeLoe, computeTiles } from "./docData.js";
import { computeCrm } from "./crmBlock.js";
import { countPendingReview } from "./sectionDraft.js";
import { localToday } from "./period.js";

const ALREADY_SENT = "Someone already sent this meeting — reopen it to send again.";
const RECORD_FAILED = "The email was sent, but the Ops Hub couldn’t record it — don’t send it again.";
const CLIENT_LOAD_ERROR = "Couldn’t load the client’s contacts — add recipients by hand.";
const PRECHECK_FAILED = "Couldn’t check the meeting before sending — nothing was sent. Try again.";
const PREVIEW_STALE = "This meeting changed since you opened the preview — check it, then send again.";
const CONNECT_FAILED = "Microsoft 365 wasn’t connected.";
const pendingMessage = (n) => `${n} item${n === 1 ? "" : "s"} still need${n === 1 ? "s" : ""} review — resolve them before sending.`;
// A recordSend write can land on an already-updated document (a stage 2 save racing this one) or
// drop mid-flight — both are worth one automatic retry with the same payload. 413 (the meeting doc
// is over its byte cap) gets its own retry, without the html body, instead.
const TRANSIENT_RECORD_STATUS = new Set([0, 409]);

const chip = { background: colors.surfaceAlt, border: `1px solid ${colors.border}`, borderRadius: 14, padding: "3px 8px", fontSize: 12, display: "inline-flex", alignItems: "center", gap: 6, color: colors.text };
const chipRemoveBtn = { background: "none", border: "none", color: colors.textMuted, cursor: "pointer", fontSize: 11, padding: 0, lineHeight: 1 };
const label = { fontSize: 11, fontWeight: 700, color: colors.textDim, letterSpacing: 1, textTransform: "uppercase", marginBottom: 4 };

export default function SendPanel({ meeting, clientName, users, current, objectives, tasks, proposals, crmData = null, flash, onSent, onClose, onRefresh, onSendingChange, onSentUnrecorded }) {
  const [loadingClient, setLoadingClient] = useState(true);
  const [clientError, setClientError] = useState(false);
  const [client, setClient] = useState(null);
  const [sel, setSel] = useState({ to: [], cc: [] });
  const [subject, setSubject] = useState(() => defaultSubject(meeting, clientName));
  const [intro, setIntro] = useState("");
  const [newAddress, setNewAddress] = useState("");
  const [newTarget, setNewTarget] = useState("to");
  const [addrError, setAddrError] = useState(null);
  const [needsConnect, setNeedsConnect] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [alertMsg, setAlertMsg] = useState(null);
  const [sending, setSending] = useState(false);
  const [disabledForever, setDisabledForever] = useState(false);
  const sendingRef = useRef(false);
  const connectingRef = useRef(false);
  const headingRef = useRef(null);
  const wasConfirmOpen = useRef(false);

  // Mount only: load the client's contacts once (a failure still leaves the panel usable — the PM
  // and account owner defaults still land in CC, and recipients can be added by hand).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const c = await api.clients.get(meeting.clientId);
        if (cancelled) return;
        setClient(c);
        setSel(defaultRecipients({ pocs: c?.pocs, project: current, users }));
      } catch {
        if (cancelled) return;
        setClientError(true);
        setSel(defaultRecipients({ pocs: [], project: current, users }));
      } finally {
        if (!cancelled) setLoadingClient(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Mount only: a silent check, never interactive — only the Connect button below may prompt.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const r = await getGraphToken("send");
      if (!cancelled && !r.ok) setNeedsConnect(true);
    })();
    return () => { cancelled = true; };
  }, []);

  // Focus returns to the heading once the confirm dialog closes, whichever way it closed
  // (Cancel or Send email) — it's the panel's own natural landing spot.
  useEffect(() => {
    if (wasConfirmOpen.current && !confirmOpen) headingRef.current?.focus();
    wasConfirmOpen.current = confirmOpen;
  }, [confirmOpen]);

  const buildHtml = (m) => renderMeetingEmail(m, {
    clientName, intro, users,
    loeRows: computeLoe(objectives, tasks, { projectId: m.projectId, periodStart: m.periodStart, periodEnd: m.periodEnd, hiddenObjectiveIds: m.loe.hiddenObjectiveIds }),
    tiles: computeTiles({ tasks, objectives, proposals, sections: m.sections, periodStart: m.periodStart, periodEnd: m.periodEnd, today: localToday(), hiddenTiles: m.summary.hiddenTiles }),
    crm: crmData ? computeCrm(crmData.tracks, crmData.contacts, { periodStart: m.periodStart, periodEnd: m.periodEnd, hiddenEventIds: m.crm?.hiddenEventIds || [] }) : null,
  });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const previewHtml = useMemo(() => buildHtml(meeting), [meeting, clientName, intro, users, objectives, tasks, proposals, crmData]);

  const pending = countPendingReview(meeting.sections);

  const removeFrom = (key, address) => setSel((s) => ({ ...s, [key]: s[key].filter((r) => r.address !== address) }));

  const addAddress = () => {
    const key = newTarget;
    const other = key === "to" ? "cc" : "to";
    const r = addRecipient(sel[key], newAddress, sel[other]);
    if (!r.ok) { setAddrError(r.error); return; }
    setSel((s) => ({ ...s, [key]: r.list }));
    setNewAddress("");
    setAddrError(null);
  };
  const onAddressKeyDown = (e) => { if (e.key === "Enter") { e.preventDefault(); addAddress(); } };

  const connectClick = async () => {
    if (connectingRef.current) return;
    connectingRef.current = true;
    setConnecting(true);
    try {
      const r = await connectMicrosoft365();
      if (r.ok) {
        const tok = await getGraphToken("send");
        setNeedsConnect(!tok.ok);
      } else {
        setAlertMsg(CONNECT_FAILED);
      }
    } finally {
      connectingRef.current = false;
      setConnecting(false);
    }
  };

  const doSend = async () => {
    if (sendingRef.current) return;
    sendingRef.current = true;
    setConfirmOpen(false);
    setSending(true);
    onSendingChange?.(true);
    setAlertMsg(null);
    try {
      let latest, tok, html;
      try {
        latest = await api.meetings.get(meeting.id);
        if (latest.status === "sent") {
          flash("error", ALREADY_SENT);
          setAlertMsg(ALREADY_SENT);
          onSent(latest);
          return;
        }
        const n = countPendingReview(latest.sections);
        if (n > 0) { setAlertMsg(pendingMessage(n)); return; }
        tok = await getGraphToken("send");
        if (!tok.ok) { setNeedsConnect(true); setAlertMsg(SEND_RECONNECT); return; }
        html = buildHtml(latest);
      } catch {
        setAlertMsg(PRECHECK_FAILED);
        return;
      }
      if (html !== previewHtml) {
        onRefresh?.(latest);
        setAlertMsg(PREVIEW_STALE);
        return;
      }
      const r = await sendMeetingEmail({ token: tok.token, subject: subject.trim(), html: previewHtml, to: sel.to, cc: sel.cc, replyTo: sel.cc });
      if (!r.ok) {
        setAlertMsg(r.error);
        if (r.kind === "reconnect") setNeedsConnect(true);
        if (r.kind === "unknown") setDisabledForever(true);
        return;
      }
      const toAddrs = sel.to.map((x) => x.address);
      const ccAddrs = sel.cc.map((x) => x.address);
      const payload = { to: toAddrs, cc: ccAddrs, subject: subject.trim(), html: previewHtml };
      let log = await api.meetings.recordSend(meeting.id, payload);
      // The meeting doc has a byte cap (api/src/model/meeting-store.js); a 413 means the email
      // itself already sent, so retry once without the html body rather than losing the record. A
      // transient status (dropped request, or a 409 from another write racing this one) gets one
      // retry with the same payload instead.
      if (!log.ok) {
        if (log.status === 413) log = await api.meetings.recordSend(meeting.id, { ...payload, html: null });
        else if (TRANSIENT_RECORD_STATUS.has(log.status)) log = await api.meetings.recordSend(meeting.id, payload);
      }
      if (log.ok) {
        const count = recipientCount(sel);
        flash("success", `Sent to ${count} ${count === 1 ? "person" : "people"} from ${SHARED_MAILBOX.address}.`);
        onSent(log.data);
      } else {
        flash("error", RECORD_FAILED);
        onSentUnrecorded?.();
        onClose();
      }
    } finally {
      sendingRef.current = false;
      setSending(false);
      onSendingChange?.(false);
    }
  };

  const ccNames = sel.cc.map((r) => r.name || r.address).join(", ");
  const repliesLine = sel.cc.length > 0 ? `Replies go to ${ccNames}` : `Replies go to ${SHARED_MAILBOX.address}`;
  const sendDisabled = sel.to.length === 0 || !subject.trim() || sending || disabledForever || pending > 0;

  return (
    <div style={{ background: colors.surface, border: `1px solid ${colors.border}`, borderRadius: 12, padding: 20, marginBottom: 16 }}>
      <div ref={headingRef} tabIndex={-1} style={{ fontSize: 15, fontWeight: 700, color: colors.text, marginBottom: 12, outline: "none" }}>Email meeting</div>
      {pending > 0 && <div style={{ fontSize: 13, color: colors.warning, marginBottom: 10 }}>{pendingMessage(pending)}</div>}
      {loadingClient && <div style={{ color: colors.textMuted, fontSize: 13, marginBottom: 10 }}>Loading recipients…</div>}
      {!loadingClient && clientError && <div style={{ color: colors.textMuted, fontSize: 13, marginBottom: 10 }}>{CLIENT_LOAD_ERROR}</div>}
      {!loadingClient && (
        <div style={{ marginBottom: 12 }}>
          {["to", "cc"].map((key) => (
            <div key={key} style={{ marginBottom: 8 }}>
              <div style={label}>{key === "to" ? "To" : "Cc"}</div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                {sel[key].map((r) => (
                  <span key={r.address} style={chip}>
                    {r.name ? <span>{r.name}</span> : null}
                    <span>{r.address}</span>
                    <button type="button" aria-label={`Remove ${r.address}`} onClick={() => removeFrom(key, r.address)} style={chipRemoveBtn}>✕</button>
                  </span>
                ))}
              </div>
            </div>
          ))}
          <div style={{ display: "flex", gap: 8, marginTop: 8, alignItems: "center" }}>
            <input aria-label="Add an address" style={{ ...inputStyle, flex: 1 }} value={newAddress} onChange={(e) => setNewAddress(e.target.value)} onKeyDown={onAddressKeyDown} />
            <select aria-label="Add to" value={newTarget} onChange={(e) => setNewTarget(e.target.value)} style={{ ...inputStyle, width: "auto" }}>
              <option value="to">To</option>
              <option value="cc">Cc</option>
            </select>
            <button type="button" onClick={addAddress} style={ghostBtn}>Add</button>
          </div>
          {addrError && <div role="alert" style={{ color: colors.danger, fontSize: 12, marginTop: 4 }}>{addrError}</div>}
        </div>
      )}
      <div style={{ marginBottom: 12 }}>
        <div style={label}>Subject</div>
        <input aria-label="Subject" maxLength={300} style={inputStyle} value={subject} onChange={(e) => setSubject(e.target.value)} />
      </div>
      <div style={{ marginBottom: 16 }}>
        <div style={label}>Intro note</div>
        <textarea aria-label="Intro note" maxLength={2000} placeholder="Optional note above the summary" style={{ ...inputStyle, minHeight: 80, resize: "vertical" }} value={intro} onChange={(e) => setIntro(e.target.value)} />
      </div>
      <div style={{ fontSize: 12, color: colors.textDim, marginBottom: 6 }}>Preview — exactly what the client receives</div>
      <iframe title="Email preview" sandbox="" srcDoc={previewHtml} style={{ width: "100%", height: "min(640px, 70vh)", border: `1px solid ${colors.border}`, background: "#fff" }} />
      <div style={{ marginTop: 16, display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
        <div style={{ fontSize: 12, color: colors.textMuted, flex: 1 }}>{`From: ${SHARED_MAILBOX.name} <${SHARED_MAILBOX.address}> · ${repliesLine}`}</div>
        {needsConnect && <button type="button" onClick={connectClick} disabled={sending || connecting} style={ghostBtn}>Connect Microsoft 365</button>}
        <button type="button" onClick={onClose} disabled={sending} style={ghostBtn}>Cancel</button>
        <button type="button" onClick={() => setConfirmOpen(true)} disabled={sendDisabled} style={{ ...primaryBtn, opacity: sendDisabled ? 0.5 : 1, cursor: sendDisabled ? "not-allowed" : "pointer" }}>{sending ? "Sending…" : "Send…"}</button>
      </div>
      {alertMsg && <div role="alert" style={{ color: colors.danger, fontSize: 13, marginTop: 8 }}>{alertMsg}</div>}
      {confirmOpen && (
        <ConfirmDialog title={`${confirmLine(sel, client?.pocs || [], clientName)}?`} confirmLabel="Send email" tone="primary" onCancel={() => setConfirmOpen(false)} onConfirm={doSend}>
          <div>To: {sel.to.map((r) => r.address).join(", ") || "—"}</div>
          <div>Cc: {sel.cc.map((r) => r.address).join(", ") || "—"}</div>
        </ConfirmDialog>
      )}
    </div>
  );
}
